<template>
    <div class="tw-w-full tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-gray-200 tw-overflow-hidden tw-flex tw-flex-col tw-shadow-sm">
        <!-- Chat Header -->
        <div
            class="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-gray-50 tw-to-white tw-border-b tw-border-solid"
            :class="isChatExpanded ? 'tw-border-gray-200' : 'tw-border-transparent'"
        >
            <div class="tw-flex tw-items-center tw-gap-3">
                <!-- Company Logo -->
                <div class="tw-w-10 tw-h-10 tw-bg-white tw-rounded-xl tw-flex-shrink-0 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-gray-200 tw-shadow-sm">
                    <img
                        v-if="companyLogo"
                        :src="companyLogo"
                        :alt="companyName"
                        class="tw-w-full tw-h-full tw-object-contain"
                    />
                    <icon v-else icon="solar:buildings-2-bold-duotone" class="tw-text-primary-500 tw-text-lg" />
                </div>
                <div class="tw-flex-1 tw-min-w-0">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate">{{ companyName || 'Company' }}</h3>
                    <p v-if="companyLocation" class="tw-text-xs tw-text-gray-400 tw-truncate tw-flex tw-items-center tw-gap-1 tw-mt-0.5">
                        <icon icon="mingcute:location-fill" class="tw-text-xs tw-flex-shrink-0 tw-text-primary-400" />
                        {{ companyLocation }}
                    </p>
                </div>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <!-- Schedule Meeting -->
                    <button
                        @click="openMeetingPopup"
                        class="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-primary-600 hover:tw-bg-primary-50 tw-transition-all"
                        title="Schedule a meeting"
                    >
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-base" />
                    </button>
                    <button
                        @click="toggleChat"
                        class="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-all"
                        :title="isChatExpanded ? 'Collapse chat' : 'Expand chat'"
                    >
                        <icon
                            :icon="isChatExpanded ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'"
                            class="tw-text-base"
                        />
                    </button>
                </div>
            </div>
        </div>

        <!-- Chat Content -->
        <div v-if="isChatExpanded" class="tw-flex tw-flex-col tw-flex-1">
            <!-- Loading State -->
            <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-py-10">
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2.5">
                    <icon icon="line-md:loading-twotone-loop" class="tw-text-2xl tw-text-primary-500" />
                    <p class="tw-text-xs tw-text-gray-400">Loading conversation...</p>
                </div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="tw-flex tw-items-center tw-justify-center tw-py-10">
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
                    <div class="tw-w-10 tw-h-10 tw-bg-red-50 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                        <icon icon="solar:danger-triangle-bold-duotone" class="tw-text-lg tw-text-red-400" />
                    </div>
                    <p class="tw-text-xs tw-text-red-500 tw-max-w-[200px]">{{ error }}</p>
                    <button
                        @click="initConversation"
                        class="tw-mt-1 tw-px-4 tw-py-1.5 tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-lg hover:tw-bg-primary-600 tw-transition-colors tw-font-medium"
                    >
                        Try Again
                    </button>
                </div>
            </div>

            <!-- Messages Area -->
            <div v-else ref="messagesContainer" class="tw-flex-1 tw-px-4 tw-py-3 tw-overflow-y-auto tw-space-y-2 tw-max-h-[380px] chat-messages-scroll" style="background: linear-gradient(to bottom, #fafafa, #ffffff);">

                <!-- Empty state -->
                <div v-if="messages.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-center">
                    <div class="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
                    </div>
                    <p class="tw-text-sm tw-font-medium tw-text-gray-600">Say hello!</p>
                    <p class="tw-text-xs tw-text-gray-400 tw-mt-0.5">Start the conversation with {{ companyName || 'this company' }}.</p>
                </div>

                <!-- Messages -->
                <div
                    v-for="message in messages"
                    :key="message.id"
                    :class="[
                        'tw-flex tw-items-end tw-gap-2',
                        isCurrentUser(message.sender_id) ? 'tw-flex-row-reverse' : ''
                    ]"
                >
                    <!-- Avatar -->
                    <div class="tw-flex-shrink-0 tw-w-6 tw-h-6 tw-rounded-full tw-overflow-hidden tw-mb-0.5">
                        <img
                            v-if="isCurrentUser(message.sender_id) && currentUser?.profile_image"
                            :src="currentUser.profile_image"
                            :alt="currentUser?.name"
                            class="tw-w-full tw-h-full tw-object-cover"
                        />
                        <div v-else-if="isCurrentUser(message.sender_id)" class="tw-w-full tw-h-full tw-bg-primary-500 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:user-bold-duotone" class="tw-text-white tw-text-[9px]" />
                        </div>
                        <img
                            v-else-if="message.sender?.profile_image"
                            :src="message.sender.profile_image"
                            :alt="message.sender?.name"
                            class="tw-w-full tw-h-full tw-object-cover"
                        />
                        <div v-else class="tw-w-full tw-h-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:buildings-2-bold-duotone" class="tw-text-gray-500 tw-text-[9px]" />
                        </div>
                    </div>

                    <!-- Bubble + Timestamp -->
                    <div :class="['tw-flex tw-flex-col tw-max-w-[78%]', isCurrentUser(message.sender_id) ? 'tw-items-end' : 'tw-items-start']">
                        <div :class="[
                            'tw-px-3 tw-py-2 tw-text-sm tw-leading-relaxed tw-inline-block',
                            isCurrentUser(message.sender_id)
                                ? 'tw-bg-primary-500 tw-text-white tw-rounded-2xl tw-rounded-br-sm'
                                : 'tw-bg-white tw-text-gray-800 tw-rounded-2xl tw-rounded-bl-sm tw-border tw-border-gray-200 tw-shadow-sm'
                        ]">
                            <p class="tw-whitespace-pre-wrap tw-break-words">{{ message.content }}</p>
                        </div>
                        <span class="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-px-1">{{ formatTimestamp(message.created_at) }}</span>
                    </div>
                </div>

                <!-- Typing Indicator -->
                <div v-if="isTyping" class="tw-flex tw-items-end tw-gap-2">
                    <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="solar:buildings-2-bold-duotone" class="tw-text-gray-500 tw-text-[9px]" />
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2.5 tw-bg-white tw-rounded-2xl tw-rounded-bl-sm tw-border tw-border-gray-200 tw-shadow-sm">
                        <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"></div>
                        <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.15s;"></div>
                        <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.3s;"></div>
                    </div>
                </div>
            </div>

            <!-- Message Input -->
            <div class="tw-p-3 tw-border-t tw-border-solid tw-border-gray-100 tw-bg-white">
                <!-- Enhanced Input Area -->
                <div class="tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-focus-within:tw-ring-2 tw-focus-within:tw-ring-primary-500 tw-focus-within:tw-border-primary-500 tw-transition-all">
                    <!-- Textarea -->
                    <textarea
                        v-model="messageText"
                        @keydown.enter.prevent="sendMessage"
                        @input="handleInput"
                        :disabled="sending || !conversationId"
                        placeholder="Type your message..."
                        class="tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-border-0 tw-rounded-lg tw-resize-none tw-outline-none tw-bg-transparent tw-min-h-[10px] tw-max-h-[120px] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        rows="1"
                        ref="messageInput"
                    ></textarea>

                    <!-- Input Actions -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-px-3 tw-pb-2">
                        <!-- Left Actions -->
                        <div class="tw-flex tw-items-center tw-gap-1">
                            <!-- Tag Product/Service -->
                            <button
                                @click="toggleTagPicker"
                                :class="[
                                    'tw-p-2 tw-rounded-md tw-transition-all tw-duration-200 tw-text-xs tw-flex tw-items-center tw-gap-1',
                                    showTagPicker
                                        ? 'tw-bg-primary-100 tw-text-primary-600'
                                        : 'tw-text-gray-400 hover:tw-text-primary-500 hover:tw-bg-primary-50'
                                ]"
                                title="Tag a product or service"
                            >
                                <icon icon="solar:tag-bold-duotone" class="tw-text-sm" />
                                <span class="tw-text-xs tw-hidden sm:tw-inline">Tag</span>
                            </button>
                        </div>

                        <!-- Right Actions -->
                        <div class="tw-flex tw-items-center tw-gap-1">
                            <button
                                v-if="messageText.trim()"
                                @click="clearMessage"
                                class="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded-md tw-transition-all tw-duration-200 tw-group"
                                title="Clear message"
                            >
                                <icon icon="solar:close-circle-bold-duotone" class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" />
                            </button>
                            <button
                                @click="sendMessage"
                                :disabled="!messageText.trim() || sending || !conversationId"
                                class="tw-p-2 tw-bg-primary-500 tw-text-white tw-rounded-md hover:tw-bg-primary-600 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-flex-shrink-0 tw-group tw-shadow-sm hover:tw-shadow-md"
                                title="Send message"
                            >
                                <icon
                                    v-if="sending"
                                    icon="line-md:loading-twotone-loop"
                                    class="tw-text-sm"
                                />
                                <icon
                                    v-else
                                    :icon="messageText.trim() ? 'solar:map-arrow-right-bold-duotone' : 'solar:map-arrow-right-linear'"
                                    class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform"
                                />
                            </button>
                        </div>
                    </div>

                    <!-- Tag Picker Panel -->
                    <div v-if="showTagPicker" class="tw-border-t tw-border-gray-200 tw-px-3 tw-py-2 tw-bg-gray-50 tw-rounded-b-lg">
                        <div v-if="tagPickerLoading" class="tw-flex tw-items-center tw-justify-center tw-py-3">
                            <icon icon="line-md:loading-twotone-loop" class="tw-text-lg tw-text-primary-500" />
                        </div>
                        <div v-else>
                            <!-- Products -->
                            <div v-if="companyProducts.length > 0" class="tw-mb-2">
                                <p class="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1 tw-flex tw-items-center tw-gap-1">
                                    <icon icon="solar:box-bold-duotone" class="tw-text-xs" /> Products
                                </p>
                                <div class="tw-flex tw-flex-wrap tw-gap-1">
                                    <button
                                        v-for="product in companyProducts"
                                        :key="'p-' + product.id"
                                        @click="appendTag('📦 Product', product.name)"
                                        class="tw-px-2 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-300 tw-text-gray-700 hover:tw-text-primary-700 tw-transition-all"
                                    >
                                        {{ product.name }}
                                    </button>
                                </div>
                            </div>

                            <!-- Services -->
                            <div v-if="companyServices.length > 0">
                                <p class="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1 tw-flex tw-items-center tw-gap-1">
                                    <icon icon="solar:settings-bold-duotone" class="tw-text-xs" /> Services
                                </p>
                                <div class="tw-flex tw-flex-wrap tw-gap-1">
                                    <button
                                        v-for="service in companyServices"
                                        :key="'s-' + service.id"
                                        @click="appendTag('🔧 Service', service.name)"
                                        class="tw-px-2 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-300 tw-text-gray-700 hover:tw-text-primary-700 tw-transition-all"
                                    >
                                        {{ service.name }}
                                    </button>
                                </div>
                            </div>

                            <!-- Empty -->
                            <div v-if="companyProducts.length === 0 && companyServices.length === 0" class="tw-py-2 tw-text-center">
                                <p class="tw-text-xs tw-text-gray-400">No products or services found for this company.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Reply Suggestions -->
                <div v-if="!messageText.trim() && messages.length === 0" class="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-flex-wrap">
                    <button
                        v-for="suggestion in quickReplies"
                        :key="suggestion"
                        @click="useQuickReply(suggestion)"
                        :disabled="!conversationId"
                        class="tw-px-3 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-transition-all tw-duration-200 tw-text-gray-600 hover:tw-text-primary-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {{ suggestion }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Schedule Meeting Popup -->
    <CompanyMeetingPopup
        v-if="showMeetingPopup"
        :visible="showMeetingPopup"
        :company-id="companyId"
        :company-name="companyName"
        :company-logo="companyLogo"
        @close="showMeetingPopup = false"
        @meeting-created="onMeetingCreated"
    />
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted, computed, nextTick } from 'vue';
import messagingApi from '@/api/messages';
import companyApi from '@/api/companies';
import { useAuthStore } from '@/stores';
import CompanyMeetingPopup from './CompanyMeetingPopup.vue';

// Props
const props = defineProps({
    companyName: {
        type: String,
        default: 'Company'
    },
    companyId: {
        type: [String, Number],
        required: true
    },
    companyLogo: {
        type: String,
        default: null
    },
    companyLocation: {
        type: String,
        default: null
    }
});

// Emits
const emit = defineEmits(['message-sent']);

// Stores
const authStore = useAuthStore();
const currentUser = computed(() => authStore.user);

// Reactive data
const messageText = ref('');
const messages = ref([]);
const conversationId = ref(null);
const isTyping = ref(false);
const loading = ref(false);
const sending = ref(false);
const error = ref(null);
const messagesContainer = ref(null);
const messageInput = ref(null);
const isChatExpanded = ref(true);
const echoChannel = ref(null);

// Meeting popup
const showMeetingPopup = ref(false);

// Tag picker
const showTagPicker = ref(false);
const tagPickerLoading = ref(false);
const companyProducts = ref([]);
const companyServices = ref([]);

// Quick reply suggestions
const quickReplies = ref([
    'Hello!',
    'I\'m interested in your products',
    'Can we schedule a meeting?',
    'Tell me more about your services',
    'What are your prices?'
]);

// Helpers
const isCurrentUser = (senderId) => {
    return currentUser.value && currentUser.value.id === senderId;
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        if (diffInHours < 24) {
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return '';
    }
};

// Auto-resize textarea
const handleInput = () => {
    if (messageInput.value) {
        messageInput.value.style.height = 'auto';
        messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
    }
};

// Clear message
const clearMessage = () => {
    messageText.value = '';
    if (messageInput.value) {
        messageInput.value.style.height = 'auto';
    }
};

// Use quick reply
const useQuickReply = (reply) => {
    messageText.value = reply;
    handleInput();
    messageInput.value?.focus();
};

// Scroll to bottom of messages
const scrollToBottom = () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
};

// Initialize conversation with backend
const initConversation = async () => {
    if (!props.companyId) {
        error.value = 'No company selected.';
        return;
    }

    loading.value = true;
    error.value = null;

    try {
        // Get or create the company conversation
        const convResponse = await messagingApi.conversations.getCompany(props.companyId);
        const conversation = convResponse.data?.data;

        if (!conversation?.id) {
            throw new Error('Invalid conversation response');
        }

        conversationId.value = conversation.id;

        // Fetch messages for this conversation
        const msgResponse = await messagingApi.conversations.getMessages(conversation.id, { per_page: 50 });
        const rawMessages = msgResponse.data?.data || [];

        // Sort messages oldest first
        messages.value = rawMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        // Mark conversation as read
        await messagingApi.conversations.markAsRead(conversation.id);

        scrollToBottom();

        // Set up real-time listener
        setupRealtimeListener(conversation.id);
    } catch (err) {
        console.error('Error initializing company chat:', err);
        error.value = 'Failed to load conversation. Please try again.';
    } finally {
        loading.value = false;
    }
};

// Set up real-time listening on the conversation channel
const setupRealtimeListener = (convId) => {
    try {
        if (!window.Echo) return;

        echoChannel.value = window.Echo.private(`conversation.${convId}`);

        echoChannel.value.listen('.message.sent', (message) => {
            // Skip messages sent by the current user — those are already handled
            // via optimistic UI + API response, so Pusher would cause a duplicate
            if (isCurrentUser(message.sender_id)) return;

            const exists = messages.value.some(m => m.id === message.id);
            if (!exists) {
                messages.value.push(message);
                scrollToBottom();

                // Mark as read since we're viewing the chat
                messagingApi.conversations.markAsRead(convId).catch(() => {});
            }
        });
    } catch (err) {
        console.error('Error setting up real-time listener:', err);
    }
};

// Clean up real-time listener
const cleanupRealtimeListener = () => {
    try {
        if (echoChannel.value && conversationId.value && window.Echo) {
            echoChannel.value.stopListening('.message.sent');
            window.Echo.leave(`conversation.${conversationId.value}`);
        }
    } catch (err) {
        console.error('Error cleaning up real-time listener:', err);
    }
    echoChannel.value = null;
};

// Send message via API
const sendMessage = async () => {
    const content = messageText.value.trim();
    if (!content || sending.value || !conversationId.value) return;

    sending.value = true;
    const tempText = content;
    messageText.value = '';

    if (messageInput.value) {
        messageInput.value.style.height = 'auto';
    }

    // Optimistic UI: add message immediately
    const optimisticMessage = {
        id: `temp-${Date.now()}`,
        sender_id: currentUser.value?.id,
        content: tempText,
        message_type: 'text',
        created_at: new Date().toISOString(),
        sender: currentUser.value
    };
    messages.value.push(optimisticMessage);
    scrollToBottom();

    try {
        const response = await messagingApi.messages.sendText(conversationId.value, { content: tempText });
        const sentMessage = response.data?.data;

        if (sentMessage) {
            // Replace the optimistic message with the real one
            const idx = messages.value.findIndex(m => m.id === optimisticMessage.id);
            if (idx !== -1) {
                messages.value.splice(idx, 1, sentMessage);
            }
        }

        emit('message-sent', sentMessage || optimisticMessage);
    } catch (err) {
        console.error('Error sending message:', err);
        // Remove optimistic message on error
        messages.value = messages.value.filter(m => m.id !== optimisticMessage.id);
        messageText.value = tempText; // Restore the text
    } finally {
        sending.value = false;
    }
};

// Schedule meeting popup
const openMeetingPopup = () => {
    showMeetingPopup.value = true;
};

const onMeetingCreated = (meeting) => {
    // Optionally append a message about the scheduled meeting
    const title = meeting?.data?.title || meeting?.title || 'meeting';
    const scheduled = meeting?.data?.scheduled_at || meeting?.scheduled_at;
    const dateStr = scheduled
        ? new Date(scheduled).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
        : '';
    messageText.value = `📅 I've scheduled a meeting: "${title}"${dateStr ? ` on ${dateStr}` : ''}`;
    handleInput();
    messageInput.value?.focus();
};

// Tag picker
const toggleTagPicker = async () => {
    showTagPicker.value = !showTagPicker.value;
    if (showTagPicker.value && companyProducts.value.length === 0 && companyServices.value.length === 0) {
        await fetchCompanyTagItems();
    }
};

const fetchCompanyTagItems = async () => {
    tagPickerLoading.value = true;
    try {
        const [productsRes, servicesRes] = await Promise.all([
            companyApi.compnayProducts(props.companyId),
            companyApi.companyServices(props.companyId)
        ]);
        companyProducts.value = productsRes.data?.data || productsRes.data || [];
        companyServices.value = servicesRes.data?.data || servicesRes.data || [];
    } catch (err) {
        console.error('Error fetching company products/services:', err);
    } finally {
        tagPickerLoading.value = false;
    }
};

const appendTag = (type, name) => {
    const tag = `\n${type}: ${name}`;
    messageText.value = (messageText.value.trimEnd()) + tag;
    showTagPicker.value = false;
    handleInput();
    nextTick(() => messageInput.value?.focus());
};

// Toggle chat expansion
const toggleChat = () => {
    isChatExpanded.value = !isChatExpanded.value;
};

// Lifecycle
onMounted(() => {
    initConversation();
});

onUnmounted(() => {
    cleanupRealtimeListener();
});
</script>

<style scoped>
.chat-messages-scroll::-webkit-scrollbar { width: 3px; }
.chat-messages-scroll::-webkit-scrollbar-track { background: transparent; }
.chat-messages-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
.chat-messages-scroll::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
</style>
